import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bluelagoon } from "@bluelagoon";
import SurveyQuestionOne from "./SurveyQuestionOne";
import SurveyTextQuestion from "./SurveyTextQuestion";
import SurveyThankYou from "./SurveyThankYou";
import SurveyExpired from "./SurveyExpired";
import Logo from "@ui/Logo";

export default function SurveyPage() {
    const { token } = useParams();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [valid, setValid] = useState(false);
    const [alreadySubmitted, setAlreadySubmitted] = useState(false);
    const [firstName, setFirstName] = useState("");
    const [step, setStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [direction, setDirection] = useState(1);
    const [answers, setAnswers] = useState({ disappointment: "", benefitPersona: "", mainBenefit: "", improvement: "" });

    useEffect(() => {
        Bluelagoon.validateSurveyToken({ token })
            .then((res) => {
                const data = res.data?.data;
                setValid(data?.valid || false);
                setAlreadySubmitted(data?.alreadySubmitted || false);
                setFirstName(data?.firstName || "");
            })
            .catch(() => setValid(false))
            .finally(() => setLoading(false));
    }, [token]);

    const updateAnswer = useCallback((key, value) => setAnswers((prev) => ({ ...prev, [key]: value })), []);
    const goTo = useCallback((next) => { setDirection(next > step ? 1 : -1); setStep(next); }, [step]);

    const handleSubmit = useCallback(async () => {
        setSubmitting(true);
        try {
            await Bluelagoon.submitSurveyResponse({ token, ...answers });
            goTo(4);
        } catch {
            setSubmitting(false);
        }
    }, [token, answers, goTo]);

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-[#0d0d0d]">
            <div className="animate-spin h-8 w-8 border-2 border-violet-500 border-t-transparent rounded-full" />
        </div>
    );
    if (!valid || alreadySubmitted) return <SurveyExpired alreadySubmitted={alreadySubmitted} />;

    const progress = step < 4 ? ((step + 1) / 4) * 100 : 100;
    const questions = [
        <SurveyQuestionOne key="q1" step={1} value={answers.disappointment}
            onChange={(v) => { updateAnswer("disappointment", v); setTimeout(() => goTo(1), 300); }}
            firstName={firstName} />,
        <SurveyTextQuestion key="q2" step={2} question={t("survey.q2")} placeholder={t("survey.q2Placeholder")}
            value={answers.benefitPersona} onChange={(v) => updateAnswer("benefitPersona", v)}
            onNext={() => goTo(2)} onBack={() => goTo(0)} />,
        <SurveyTextQuestion key="q3" step={3} question={t("survey.q3")} placeholder={t("survey.q3Placeholder")}
            value={answers.mainBenefit} onChange={(v) => updateAnswer("mainBenefit", v)}
            onNext={() => goTo(3)} onBack={() => goTo(1)} />,
        <SurveyTextQuestion key="q4" step={4} question={t("survey.q4")} placeholder={t("survey.q4Placeholder")}
            value={answers.improvement} onChange={(v) => updateAnswer("improvement", v)}
            onNext={handleSubmit} onBack={() => goTo(2)} isLast submitting={submitting} />,
        <SurveyThankYou key="thanks" firstName={firstName} />,
    ];

    return (
        <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
            <div className="fixed top-0 left-0 right-0 z-10">
                <div className="flex items-center px-6 py-4">
                    <Logo className="h-5 text-white" />
                </div>
                {step < 4 && (
                    <div className="h-0.5 bg-white/5">
                        <div className="h-full bg-violet-500 transition-all duration-500" style={{ width: `${progress}%` }} />
                    </div>
                )}
            </div>
            <div className="flex-1 flex items-center justify-center px-6">
                <div key={step} className="w-full max-w-2xl animate-[surveyIn_400ms_cubic-bezier(0.23,1,0.32,1)_forwards]"
                    style={{ "--dir": direction }}>{questions[step]}</div>
            </div>
        </div>
    );
}
